import { ScrollArea } from "./ui/scroll-area";
import { Badge } from "./ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { Zap, Brain, Shield, Lock, Unlock, Timer, LogIn, LogOut, X, Activity } from "lucide-react";

const ACTIVITY_ICONS = {
  decision: { automate: Zap, augment: Brain, protect: Shield },
  lock: Lock,
  unlock: Unlock,
  timer: Timer,
  joined: LogIn,
  left: LogOut,
};

const ACTIVITY_COLORS = {
  decision: "text-indigo-600 bg-indigo-50",
  lock: "text-red-600 bg-red-50",
  joined: "text-emerald-600 bg-emerald-50",
  left: "text-slate-500 bg-slate-100",
  timer: "text-amber-600 bg-amber-50",
};

const formatTime = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

export const ActivityFeed = ({ open, onOpenChange, activities = [] }) => {
  const reversed = [...activities].reverse();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[380px] p-0 bg-[#F8FAFC]" data-testid="activity-feed-panel">
        <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-md border-b border-slate-200 px-5 py-4">
          <SheetHeader className="text-left">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-indigo-500" />
                <SheetTitle className="font-heading text-lg text-slate-900">Live Activity</SheetTitle>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs text-slate-500">
                  {activities.length} events
                </Badge>
                <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} className="h-8 w-8 p-0">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </SheetHeader>
        </div>

        <ScrollArea className="h-[calc(100vh-80px)]">
          <div className="p-4 space-y-1">
            {reversed.length === 0 ? (
              <div className="text-center py-12">
                <Activity className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No activity yet</p>
                <p className="text-xs text-slate-300 mt-1">Actions will appear here in real-time</p>
              </div>
            ) : (
              reversed.map((item, idx) => {
                const colorClass = ACTIVITY_COLORS[item.type] || "text-slate-500 bg-slate-100";
                let IconComponent = Activity;
                if (item.type === "decision" && item.meta?.decision) {
                  IconComponent = ACTIVITY_ICONS.decision[item.meta.decision] || Activity;
                } else if (item.type === "lock") {
                  IconComponent = item.description?.includes("unlocked") ? Unlock : Lock;
                } else if (ACTIVITY_ICONS[item.type]) {
                  IconComponent = ACTIVITY_ICONS[item.type];
                }

                return (
                  <div
                    key={item.id || idx}
                    className="flex items-start gap-3 py-2.5 px-2 rounded-lg hover:bg-white/80 transition-colors"
                    data-testid={`activity-item-${idx}`}
                  >
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${colorClass}`}>
                      <IconComponent className="w-3.5 h-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-700 leading-snug">
                        <span className="font-semibold text-slate-900">{item.user_name}</span>
                        {" "}{item.description}
                      </p>
                      {item.meta?.department && (
                        <span className="text-[10px] text-slate-400">{item.meta.department}</span>
                      )}
                    </div>
                    <span className="text-[10px] text-slate-400 shrink-0 mt-0.5">
                      {formatTime(item.timestamp)}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default ActivityFeed;
